// ============================================================
// Classroom lead — teacher master-controls scene + audio
// ============================================================
// Live Guided Session already has a `position` broadcast (scene + yaw/pitch)
// for the 2D web viewer. Headsets must NOT follow yaw/pitch — forcing look
// direction in VR makes people sick.
//
// This module is the classroom protocol:
//   - Teacher (host tab) broadcasts `lead`: scene + narration + video clock
//   - Quest followers apply scene / play-pause-seek, and ignore rotation
//   - Headset look-around stays local
// ============================================================

export const GUIDED_CHANNEL_PREFIX = 'guided:';
export const CLASSROOM_LEAD_EVENT = 'lead';
export const CLASSROOM_POSITION_EVENT = 'position';
export const CLASSROOM_LEAD_INTERVAL_MS = 200;
/** Skip tiny clock corrections so we don't stutter audio/video every tick. */
export const CLASSROOM_SEEK_THRESHOLD_SEC = 0.45;

export interface ClassroomMediaLead {
    playing: boolean;
    timeSec: number;
    /** Clip the teacher is playing, when the follower doesn't already have it loaded. */
    audioUrl?: string | null;
    /** Narration language the teacher selected, so followers leave the primary track. */
    languageCode?: string | null;
}

export interface ClassroomLeadState {
    sceneId: string;
    narration: ClassroomMediaLead;
    video: ClassroomMediaLead | null;
    timestamp: number;
}

export function guidedChannelName(sessionId: string): string {
    return `${GUIDED_CHANNEL_PREFIX}${sessionId}`;
}

export function parseGuidedSessionId(search: string): string | null {
    const raw = search.startsWith('?') ? search : `?${search}`;
    const id = new URLSearchParams(raw).get('guided');
    const trimmed = id?.trim();
    return trimmed ? trimmed : null;
}

export function headsetGuidedUrl(origin: string, vrCode: string, sessionId: string): string {
    const base = origin.replace(/\/$/, '');
    const code = encodeURIComponent(vrCode);
    const guided = encodeURIComponent(sessionId);
    return `${base}/quest/${code}?guided=${guided}`;
}

/** 2D follower link for a Live Guided / class learning session. */
export function viewerGuidedUrl(origin: string, tourSlug: string, sessionId: string): string {
    const base = origin.replace(/\/$/, '');
    return `${base}/view/${encodeURIComponent(tourSlug)}?guided=${encodeURIComponent(sessionId)}`;
}

/** Teacher host link — same tour, broadcasts scene + look. */
export function hostGuidedUrl(origin: string, tourSlug: string, sessionId: string): string {
    return `${viewerGuidedUrl(origin, tourSlug, sessionId)}&host=1`;
}

const isMediaLead = (value: unknown): value is ClassroomMediaLead => {
    if (!value || typeof value !== 'object') return false;
    const rec = value as Record<string, unknown>;
    return typeof rec.playing === 'boolean' && typeof rec.timeSec === 'number' && Number.isFinite(rec.timeSec);
};

export function isClassroomLead(payload: unknown): payload is ClassroomLeadState {
    if (!payload || typeof payload !== 'object') return false;
    const rec = payload as Record<string, unknown>;
    if (typeof rec.sceneId !== 'string' || !rec.sceneId) return false;
    if (!isMediaLead(rec.narration)) return false;
    if (rec.video !== null && rec.video !== undefined && !isMediaLead(rec.video)) return false;
    return true;
}

export function shouldSeekMedia(
    localTimeSec: number,
    leadTimeSec: number,
    thresholdSec = CLASSROOM_SEEK_THRESHOLD_SEC,
): boolean {
    if (!Number.isFinite(localTimeSec) || !Number.isFinite(leadTimeSec)) return false;
    return Math.abs(localTimeSec - leadTimeSec) > thresholdSec;
}

let latestAudioLead: ClassroomMediaLead = { playing: false, timeSec: 0 };

/** Host-tab narration clocks write here; the guided broadcast interval reads it. */
export function reportClassroomAudioLead(lead: ClassroomMediaLead): void {
    const audioUrl = lead.audioUrl ?? latestAudioLead.audioUrl ?? null;
    const languageCode = lead.languageCode ?? latestAudioLead.languageCode ?? null;
    latestAudioLead = {
        playing: !!lead.playing,
        timeSec: Number.isFinite(lead.timeSec) ? Math.max(0, lead.timeSec) : 0,
        ...(audioUrl ? { audioUrl } : {}),
        ...(languageCode ? { languageCode } : {}),
    };
}

export function readClassroomAudioLead(): ClassroomMediaLead {
    return latestAudioLead;
}

export function buildClassroomLead(opts: {
    sceneId: string;
    video: ClassroomMediaLead | null;
    timestamp?: number;
}): ClassroomLeadState {
    return {
        sceneId: opts.sceneId,
        narration: { ...readClassroomAudioLead() },
        video: opts.video
            ? {
                playing: !!opts.video.playing,
                timeSec: Number.isFinite(opts.video.timeSec) ? Math.max(0, opts.video.timeSec) : 0,
            }
            : null,
        timestamp: opts.timestamp ?? Date.now(),
    };
}

type AudioFollowListener = (lead: ClassroomMediaLead) => void;

let latestAudioFollow: ClassroomMediaLead | null = null;

/** Test helper — drops the host clip so cases don't leak into each other. */
export function resetClassroomAudioLead(): void {
    latestAudioLead = { playing: false, timeSec: 0 };
    latestAudioFollow = null;
}

const audioFollowListeners = new Set<AudioFollowListener>();
let classroomAudioBlocked = false;
const audioBlockedListeners = new Set<(blocked: boolean) => void>();

/** Follower tabs: the teacher's narration clock, last broadcast received. */
export function readClassroomAudioFollow(): ClassroomMediaLead | null {
    return latestAudioFollow;
}

export function publishClassroomAudioFollow(lead: ClassroomMediaLead): void {
    latestAudioFollow = {
        playing: !!lead.playing,
        timeSec: Number.isFinite(lead.timeSec) ? Math.max(0, lead.timeSec) : 0,
        audioUrl: lead.audioUrl ?? null,
        ...(lead.languageCode ? { languageCode: lead.languageCode } : {}),
    };
    for (const listener of audioFollowListeners) listener(latestAudioFollow);
}

export function subscribeClassroomAudioFollow(listener: AudioFollowListener): () => void {
    audioFollowListeners.add(listener);
    if (latestAudioFollow) listener(latestAudioFollow);
    return () => { audioFollowListeners.delete(listener); };
}

/** True when a follower's browser blocked play() until someone taps. */
export function setClassroomAudioBlocked(blocked: boolean): void {
    if (classroomAudioBlocked === blocked) return;
    classroomAudioBlocked = blocked;
    for (const listener of audioBlockedListeners) listener(blocked);
}

export function subscribeClassroomAudioBlocked(listener: (blocked: boolean) => void): () => void {
    audioBlockedListeners.add(listener);
    listener(classroomAudioBlocked);
    return () => { audioBlockedListeners.delete(listener); };
}
